import React from "react";
import { useQuery } from "@tanstack/react-query";
import { endpoints } from "@/api";
import { useI18n } from "@/i18n";
import { ShieldCheck, Lock } from "lucide-react";

export default function AdminUsageTab() {
  const { isRTL } = useI18n();
  const { data, isLoading } = useQuery({
    queryKey: ["mycafe-admin-usage"], queryFn: endpoints.myCafeAdminUsage,
  });

  if (isLoading) return <div className="text-[#5C544F] text-sm">{isRTL ? "جارٍ التحميل..." : "Loading..."}</div>;

  const totals = data?.totals || {};
  const users = data?.users || [];

  return (
    <div className="space-y-6" data-testid="mycafe-admin-usage">
      <div className="rounded-xl bg-amber-50 border border-amber-200 p-4 flex items-start gap-3">
        <Lock className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" />
        <div className="text-sm text-amber-900">
          {isRTL
            ? "كأدمن، تستطيع رؤية أعداد الاستخدام فقط (كم مستخدم نشط، كم وصفة، كم زيارة) — محتوى الوصفات والتقارير الخاصة بكل مستخدم محمي ولا يظهر هنا."
            : "As admin, you can only see usage counts (active users, recipes, visits). The actual recipe and visit content of each user is private and never shown here."}
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
        <Kpi label={isRTL ? "نشطون" : "Active users"} value={totals.active_users || 0} />
        <Kpi label={isRTL ? "الفروع" : "Branches"} value={totals.branches || 0} />
        <Kpi label={isRTL ? "الزيارات" : "Visits"} value={totals.visits || 0} />
        <Kpi label={isRTL ? "إسبريسو" : "Espresso"} value={totals.espresso || 0} />
        <Kpi label={isRTL ? "قهوة اليوم" : "Drip"} value={totals.drip || 0} />
        <Kpi label={isRTL ? "مخصصة" : "Custom"} value={totals.custom || 0} />
        <Kpi label={isRTL ? "موظفون" : "Team links"} value={totals.team || 0} />
      </div>

      <div>
        <h3 className="text-xs uppercase tracking-[0.25em] text-[#C86240] mb-3 flex items-center gap-2">
          <ShieldCheck className="w-3.5 h-3.5" />
          {isRTL ? "حسب المستخدم" : "By user"}
        </h3>
        {users.length === 0 ? (
          <div className="text-sm text-[#5C544F]">{isRTL ? "لا توجد بيانات بعد" : "No usage yet"}</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#2A2421] text-[#F9F6F0]">
                <tr>
                  <th className="text-start p-2">{isRTL ? "المستخدم" : "User"}</th>
                  <th className="p-2">{isRTL ? "فروع" : "Br."}</th>
                  <th className="p-2">{isRTL ? "زيارات" : "Visits"}</th>
                  <th className="p-2">{isRTL ? "إسبريسو" : "Esp."}</th>
                  <th className="p-2">{isRTL ? "قهوة اليوم" : "Drip"}</th>
                  <th className="p-2">{isRTL ? "مخصصة" : "Cust."}</th>
                  <th className="p-2">{isRTL ? "موظفون" : "Team"}</th>
                  <th className="p-2">{isRTL ? "الإجمالي" : "Total"}</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.user_id} className="border-b border-[#2A2421]/10" data-testid={`admin-row-${u.user_id}`}>
                    <td className="p-2">
                      <div className="text-[#2A2421]">{u.name || "—"}</div>
                      <div className="text-xs text-[#5C544F]">{u.email}</div>
                    </td>
                    <td className="p-2 text-center">{u.stats.branches || 0}</td>
                    <td className="p-2 text-center">{u.stats.visits || 0}</td>
                    <td className="p-2 text-center">{u.stats.espresso || 0}</td>
                    <td className="p-2 text-center">{u.stats.drip || 0}</td>
                    <td className="p-2 text-center">{u.stats.custom || 0}</td>
                    <td className="p-2 text-center">{u.stats.team || 0}</td>
                    <td className="p-2 text-center font-bold text-[#C86240]">{u.total}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

const Kpi = ({ label, value }) => (
  <div className="soft-card rounded-xl p-4 text-center">
    <div className="text-xs uppercase tracking-widest text-[#5C544F]">{label}</div>
    <div className="text-3xl font-bold text-[#2A2421] mt-1">{value}</div>
  </div>
);
